import DatePicker from "@/components/DatePicker"
import MonthPicker from "@/components/MonthPicker"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import WeekPicker from "@/components/WeekPicker"
import {
  ChartViewModeEnum,
  computeIntervalFlowInDay,
  computeIntervalFlowInMonth,
  computeIntervalFlowInWeek,
  createIntervalFlowStore,
  FetchType,
} from "@/stores/intervalFlowStore"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { Download } from "lucide-react"
import { FC, Suspense, useEffect, useRef, useMemo } from "react"
import { useShallow } from "zustand/react/shallow"
import jsPDF from "jspdf"
import ReactECharts from "echarts-for-react"
import NewChart2 from "@/components/NewChart2"
import { useChartData } from "@/hooks/use-chart-data"

const downloadFile = (content: string, filename: string, mime: string) => {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
const useIntervalFlowStore = createIntervalFlowStore()

export type ConsumptionDataProps = {
  deviceId: number
  councilId: string
  unavailableReason?: unknown
}

type DataUnavailableReason = {
  code?: string
  message?: string
  typeGroup?: string
}

const ConsumptionData: FC<ConsumptionDataProps> = (props) => {
  const chartRef = useRef<ReactECharts>(null)
  const unavailableReason = props.unavailableReason as DataUnavailableReason | undefined
  const isMeasurementMappingMissing = unavailableReason?.code === "MEASUREMENT_MAPPING_NOT_FOUND"
  const unavailableTitle = isMeasurementMappingMissing
    ? "Device has no measurement mapping"
    : "Consumption data unavailable"

  const {
    fetchType,
    selectedDate,
    selectedMonth,
    selectedWeek,
    chartViewMode,
    flowMeasurementData,
    setCouncilId,
    setDeviceId,
    loadIntervalFlowMeasurement,
    setChartViewMode,
    setSelectedDate,
    setSelectedMonth,
    setSelectedWeek,
  } = useIntervalFlowStore(
    useShallow((state) => ({
      fetchType: state.fetchType,
      selectedDate: state.selectedDate,
      selectedMonth: state.selectedMonth,
      selectedWeek: state.selectedWeek,
      chartViewMode: state.chartViewMode,
      flowMeasurementData: state.flowMeasurementData,
      setCouncilId: state.setCouncilId,
      setDeviceId: state.setDeviceId,
      loadIntervalFlowMeasurement: state.loadIntervalFlowMeasurement,
      setChartViewMode: state.setChartViewMode,
      setSelectedDate: state.setSelectedDate,
      setSelectedMonth: state.setSelectedMonth,
      setSelectedWeek: state.setSelectedWeek,
    })),
  )

  const intervalFlowInDay = useMemo(
    () => computeIntervalFlowInDay(selectedDate, flowMeasurementData),
    [selectedDate, flowMeasurementData],
  )
  const intervalFlowInMonth = useMemo(
    () => computeIntervalFlowInMonth(selectedMonth, flowMeasurementData),
    [selectedMonth, flowMeasurementData],
  )
  const intervalFlowInWeek = useMemo(
    () => computeIntervalFlowInWeek(selectedWeek, flowMeasurementData),
    [selectedWeek, flowMeasurementData],
  )

  const hourlyChartData = useChartData(
    intervalFlowInDay,
    "hourly",
    selectedDate,
    selectedMonth,
    selectedWeek,
  )
  const dailyChartData = useChartData(
    intervalFlowInMonth,
    "daily",
    selectedDate,
    selectedMonth,
    selectedWeek,
  )
  const weeklyChartData = useChartData(
    intervalFlowInWeek,
    "weekly",
    selectedDate,
    selectedMonth,
    selectedWeek,
  )

  useEffect(() => {
    if (unavailableReason) return
    setCouncilId(props.councilId)
    setDeviceId(props.deviceId)
    loadIntervalFlowMeasurement()
  }, [
    props.councilId,
    props.deviceId,
    setCouncilId,
    setDeviceId,
    loadIntervalFlowMeasurement,
    unavailableReason,
  ])

  function getCurrentDataByMode(
    mode: ChartViewModeEnum,
    dayData: typeof intervalFlowInDay,
    monthData: typeof intervalFlowInMonth,
    weekData: typeof intervalFlowInWeek,
  ) {
    switch (mode) {
      case ChartViewModeEnum.Hourly:
        return dayData
      case ChartViewModeEnum.Daily:
        return monthData
      case ChartViewModeEnum.Weekly: {
        const startOfWeek = selectedWeek.startOf("week").startOf("day")
        const endOfWeek = selectedWeek.plus({ weeks: 1 }).startOf("week").startOf("day")
        return weekData.filter((item) => item.time >= startOfWeek && item.time <= endOfWeek)
      }
      default:
        return []
    }
  }

  const getCurrentData = () =>
    getCurrentDataByMode(chartViewMode, intervalFlowInDay, intervalFlowInMonth, intervalFlowInWeek)

  const unit = getCurrentData()[0]?.unit ?? "L"

  const exportJSON = () => {
    const data = getCurrentData()
    const json = JSON.stringify(
      data.map((d) => ({ time: d.time.toISO(), value: d.index })),
      null,
      2,
    )
    downloadFile(json, "consumption.json", "application/json")
  }

  const exportCSV = () => {
    const data = getCurrentData()
    const csv = "Time,Value\n" + data.map((d) => `${d.time.toISO()},${d.index}`).join("\n")
    downloadFile(csv, "consumption.csv", "text/csv")
  }

  const exportImage = () => {
    const chart = chartRef.current?.getEchartsInstance()
    if (chart) {
      const dataURL = chart.getDataURL({ type: "png", pixelRatio: 2 })
      const a = document.createElement("a")
      a.href = dataURL
      a.download = "chart.png"
      a.click()
    }
  }

  const exportPDF = () => {
    const data = getCurrentData()
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()

    // Header
    doc.setFontSize(18)
    doc.text("Water Consumption Report", pageWidth / 2, 20, {
      align: "center",
    })
    doc.setFontSize(12)
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 10, 35)
    doc.text(`View Mode: ${chartViewMode}`, 10, 45)
    doc.text(`Device: Unknown`, 10, 50)
    doc.text(`Serial Number: Unknown`, 10, 55)

    // Date Range
    let dateRange = ""
    switch (chartViewMode) {
      case ChartViewModeEnum.Hourly:
        dateRange = `Date: ${selectedDate.toFormat("yyyy-MM-dd")}`
        break
      case ChartViewModeEnum.Daily:
        dateRange = `Month: ${selectedMonth.toFormat("yyyy-MM")}`
        break
      case ChartViewModeEnum.Weekly:
        dateRange = `Week: ${selectedWeek.toFormat("yyyy-MM-dd")} to ${selectedWeek.endOf("week").toFormat("yyyy-MM-dd")}`
        break
    }
    doc.text(dateRange, 10, 60)

    // Table Header
    let y = 75
    doc.setFontSize(12)
    doc.setFont("courier", "bold")
    doc.text("S.No", 10, y)
    doc.text("Time", 35, y)
    doc.text("Consumption (L)", 120, y)
    doc.line(10, y + 3, pageWidth - 10, y + 3) // Horizontal line
    y += 12

    data.forEach((d, index) => {
      if (y > pageHeight - 20) {
        doc.addPage()
        y = 20
        // Re-add header on new page
        doc.setFontSize(12)
        doc.setFont("courier", "bold")
        doc.text("S.No", 10, y)
        doc.text("Time", 35, y)
        doc.text("Consumption (L)", 120, y)
        doc.line(10, y + 3, pageWidth - 10, y + 3)
        y += 12
      }
      doc.setFontSize(10)
      doc.setFont("courier", "normal")
      doc.text((index + 1).toString(), 10, y)
      doc.text(d.time.toFormat("yyyy-MM-dd HH:mm"), 35, y)
      doc.text(d.index.toString(), 120, y)
      y += 8
    })

    doc.save("consumption.pdf")
  }

  if (unavailableReason) {
    return (
      <div className="lg:col-span-2">
          <div className="rounded-lg border border-[#d6dbe3] bg-white p-4">
            <Alert>
              <AlertTitle>{unavailableTitle}</AlertTitle>
              <AlertDescription>
                {unavailableReason.message ?? "Consumption data cannot be displayed for this device."}
              </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="lg:col-span-2">
      <Tabs
        defaultValue="hourly"
        onValueChange={(value) => setChartViewMode(value as ChartViewModeEnum)}
      >
        <div className="mb-4 flex flex-col gap-4 rounded-lg border border-[#d6dbe3] bg-white px-3 py-2 md:flex-row md:items-center md:justify-between">
          <TabsList>
            <TabsTrigger
              value="hourly"
              className="text-black data-[state=active]:bg-[#2bc8db] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-black"
            >
              Day
            </TabsTrigger>
            <TabsTrigger
              value="weekly"
              className="text-black data-[state=active]:bg-[#2bc8db] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-black"
            >
              Week
            </TabsTrigger>
            <TabsTrigger
              value="daily"
              className="text-black data-[state=active]:bg-[#2bc8db] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-black"
            >
              Month
            </TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-4">
            <>
              {chartViewMode === ChartViewModeEnum.Hourly && (
                <DatePicker
                  date={selectedDate.toJSDate()}
                  setDate={(v) => {
                    if (v) {
                      setSelectedDate(v)
                    }
                  }}
                  className="w-full md:w-auto"
                />
              )}

              {chartViewMode === ChartViewModeEnum.Daily && (
                <MonthPicker
                  date={selectedMonth.toJSDate()}
                  setDate={(v) => {
                    if (v) {
                      setSelectedMonth(v)
                    }
                  }}
                  className="w-full md:w-auto"
                />
              )}

              {chartViewMode === ChartViewModeEnum.Weekly && (
                <WeekPicker
                  date={selectedWeek.toJSDate()}
                  setDate={(v) => {
                    if (v) {
                      setSelectedWeek(v)
                    }
                  }}
                  className="w-full md:w-auto"
                />
              )}
            </>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="mr-2 size-4" />
                  Export
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-52" align="start">
                <div className="flex flex-col gap-2">
                  <Button variant="ghost" size="sm" className="justify-start" onClick={exportJSON}>
                    Export Data (JSON)
                  </Button>
                  <Button variant="ghost" size="sm" className="justify-start" onClick={exportCSV}>
                    Export Data (CSV)
                  </Button>
                  <Button variant="ghost" size="sm" className="justify-start" onClick={exportPDF}>
                    Export as PDF
                  </Button>
                  <Button variant="ghost" size="sm" className="justify-start" onClick={exportImage}>
                    Export as Image
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            {fetchType !== FetchType.final && (
              <div className="flex items-center gap-2 text-sm text-[#2bc8db]">
                <LoadingSpinner size="sm" className="border-t-[#2bc8db]" />
                <span>Loading data...</span>
              </div>
            )}
          </div>
        </div>
        <TabsContent value="hourly" className="relative space-y-4">
          {fetchType !== FetchType.final && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/50 backdrop-blur-sm">
              <LoadingSpinner size="lg" className="border-t-[#2bc8db]" />
              <p className="mt-2 text-[#2bc8db] font-medium">Fetching measurements...</p>
            </div>
          )}
          <NewChart2
            ref={chartRef}
            chartData={hourlyChartData}
            viewMode={ChartViewModeEnum.Hourly}
            selectedDate={selectedDate}
            selectedMonth={selectedMonth}
            selectedWeek={selectedWeek}
            unit={unit}
            totalFlowUnit={unit}
          />
        </TabsContent>
        <TabsContent value="daily" className="relative space-y-4">
          {fetchType !== FetchType.final && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/50 backdrop-blur-sm">
              <LoadingSpinner size="lg" className="border-t-[#2bc8db]" />
              <p className="mt-2 text-[#2bc8db] font-medium">Fetching measurements...</p>
            </div>
          )}
          <NewChart2
            ref={chartRef}
            chartData={dailyChartData}
            viewMode={ChartViewModeEnum.Daily}
            selectedDate={selectedDate}
            selectedMonth={selectedMonth}
            selectedWeek={selectedWeek}
            unit={unit}
            totalFlowUnit={unit}
          />
        </TabsContent>
        <TabsContent value="weekly" className="relative space-y-4">
          <Suspense fallback={<div>Loading chart...</div>}>
            {fetchType !== FetchType.final && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/50 backdrop-blur-sm">
                <LoadingSpinner size="lg" className="border-t-[#2bc8db]" />
                <p className="mt-2 text-[#2bc8db] font-medium">Fetching measurements...</p>
              </div>
            )}
            <NewChart2
              ref={chartRef}
              chartData={weeklyChartData}
              viewMode={ChartViewModeEnum.Weekly}
              selectedDate={selectedDate}
              selectedMonth={selectedMonth}
              selectedWeek={selectedWeek}
              unit={unit}
              totalFlowUnit={unit}
            />
          </Suspense>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default ConsumptionData
